import React, { useState, useEffect, useRef } from "react";
import { doc, writeBatch } from "firebase/firestore";
import { db } from "../../firebase";
import AdminModal from "../../components/AdminModal";
import { useToast } from "../../components/Toast";
import { FaExclamationTriangle, FaCheckCircle } from "react-icons/fa";
import {
  colors, spacing, typography, radius, btnPrimary, btnSecondary, inputBase,
} from "../../utils/designTokens";

const EMPTY = { nombre: "", apellido: "", email: "", ci: "", cel: "" };

const hasAnyData = (p) => p && (p.nombre || p.apellido || p.email || p.ci || p.cel);
const isMissingEmail = (p) => hasAnyData(p) && !p.email;

function fromUser(user) {
  if (!user) return { ...EMPTY };
  const parts = (user.name || user.NombreCompleto || "").split(" ");
  return {
    nombre: parts[0] || "",
    apellido: parts.slice(1).join(" "),
    email: user.email || "",
    ci: user.ci || user.documento || "",
    cel: user.cel || user.phone || "",
  };
}

// Celda de input en la tabla
function Cell({ value, onChange, placeholder, type = "text", disabled, warn }) {
  return (
    <td style={{ padding: "3px 4px", borderRight: `1px solid ${colors.gray200}` }}>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: "100%",
          border: `1px solid ${warn ? colors.warning : disabled ? "transparent" : colors.gray200}`,
          borderRadius: 4,
          padding: "4px 7px",
          fontSize: 13,
          fontFamily: typography.fontFamily,
          background: disabled ? colors.gray50 : colors.white,
          color: disabled ? colors.gray400 : colors.gray900,
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </td>
  );
}

/**
 * Modal planilla para cargar propietarios/inquilinos sin lanzar invitación.
 * Igual al estilo de RevisionUnidadesModal.
 */
const EditarPropietariosModal = ({ open, onClose, units, usersMap = {}, onSaved }) => {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const inicial = units.map((u) => ({
      id: u.id,
      number: u.number,
      floor: u.floor,
      hasOwner: !!u.owner,
      hasTenant: !!u.tenant,
      ownerData: u.ownerData
        ? { ...EMPTY, ...u.ownerData }
        : fromUser(usersMap[String(u.owner)]),
      tenantData: u.tenantData
        ? { ...EMPTY, ...u.tenantData }
        : fromUser(usersMap[String(u.tenant)]),
    }));
    inicial.sort((a, b) => a.floor - b.floor || String(a.number).localeCompare(String(b.number)));
    setRows(inicial);
  }, [open]);

  const setField = (id, persona, field, val) =>
    setRows((prev) => prev.map((r) =>
      r.id === id ? { ...r, [persona]: { ...r[persona], [field]: val } } : r
    ));

  const warnings = rows.filter((r) => isMissingEmail(r.ownerData) || isMissingEmail(r.tenantData));

  const handleSave = async () => {
    setSaving(true);
    try {
      const batch = writeBatch(db);
      rows.forEach((r) => {
        const upd = {};
        if (hasAnyData(r.ownerData) || r.hasOwner) upd.ownerData = r.ownerData;
        if (hasAnyData(r.tenantData) || r.hasTenant) upd.tenantData = r.tenantData;
        if (Object.keys(upd).length) batch.update(doc(db, "apartments", r.id), upd);
      });
      await batch.commit();
      const saved = rows.filter((r) => hasAnyData(r.ownerData) || hasAnyData(r.tenantData)).length;
      showToast(
        `${saved} unidad${saved !== 1 ? "es" : ""} guardada${saved !== 1 ? "s" : ""}.${warnings.length ? ` ${warnings.length} con datos incompletos (sin email).` : ""}`,
        warnings.length ? "warning" : "success"
      );
      if (onSaved) onSaved(rows);
      onClose();
    } catch (err) {
      console.error("[EditarPropietarios]", err);
      showToast(`Error: ${err?.code || err?.message || "desconocido"}`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Editar propietarios e inquilinos"
      subtitle={`${rows.length} unidades · Ningún campo es obligatorio · Los datos se guardan sin enviar invitación`}
      size="full"
      footer={
        <div style={{ display: "flex", gap: spacing[2], justifyContent: "space-between", width: "100%", alignItems: "center" }}>
          <span style={{ fontSize: typography.sizes.sm, color: warnings.length ? colors.warning : colors.gray400 }}>
            {warnings.length > 0
              ? <><FaExclamationTriangle style={{ marginRight: 4, verticalAlign: "middle" }} />{warnings.length} unidad{warnings.length > 1 ? "es" : ""} con datos sin email</>
              : null}
          </span>
          <div style={{ display: "flex", gap: spacing[2] }}>
            <button style={btnSecondary} onClick={onClose} disabled={saving}>Cancelar</button>
            <button style={btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? "Guardando…" : `Guardar ${rows.length} unidades`}
            </button>
          </div>
        </div>
      }
    >
      <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(94vh - 180px)" }}>
        <table style={{ borderCollapse: "collapse", width: "max-content", minWidth: "100%", fontSize: 13 }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
            <tr>
              <th style={{ ...thBase, width: 70 }} rowSpan={2}>NÚMERO</th>
              <th style={{ ...thBase, width: 52 }} rowSpan={2}>PISO</th>
              <th style={{ ...thBase, width: 24 }} rowSpan={2}></th>
              {/* Propietario */}
              <th style={{ ...thBase, ...thGroup, background: "#3b5fc0", borderLeft: `2px solid ${colors.white}` }} colSpan={5}>
                PROPIETARIO
              </th>
              {/* Inquilino */}
              <th style={{ ...thBase, ...thGroup, background: "#1e7a56", borderLeft: `2px solid ${colors.white}` }} colSpan={5}>
                INQUILINO
              </th>
            </tr>
            <tr>
              {["NOMBRE", "APELLIDO", "EMAIL", "C.I.", "CELULAR"].map((h) => (
                <th key={`p-${h}`} style={{ ...thBase, background: "#4a6fd4", minWidth: h === "EMAIL" ? 180 : 110, borderLeft: `1px solid rgba(255,255,255,0.2)` }}>{h}</th>
              ))}
              {["NOMBRE", "APELLIDO", "EMAIL", "C.I.", "CELULAR"].map((h) => (
                <th key={`i-${h}`} style={{ ...thBase, background: "#279166", minWidth: h === "EMAIL" ? 180 : 110, borderLeft: `1px solid rgba(255,255,255,0.2)` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const warnOwner = isMissingEmail(row.ownerData);
              const warnTenant = isMissingEmail(row.tenantData);
              const bg = idx % 2 === 0 ? colors.white : colors.gray50;
              const o = row.ownerData;
              const t = row.tenantData;
              const setO = (f) => (v) => setField(row.id, "ownerData", f, v);
              const setT = (f) => (v) => setField(row.id, "tenantData", f, v);

              return (
                <tr key={row.id} style={{ background: bg }}>
                  {/* Número y piso */}
                  <td style={tdFixed}>{row.number}</td>
                  <td style={tdFixed}>{row.floor}</td>
                  {/* Indicador */}
                  <td style={{ ...tdFixed, width: 24, textAlign: "center", padding: "3px 2px" }}>
                    {(warnOwner || warnTenant)
                      ? <FaExclamationTriangle size={11} style={{ color: colors.warning }} title="Faltan datos" />
                      : (hasAnyData(o) && o.email)
                        ? <FaCheckCircle size={11} style={{ color: colors.success }} />
                        : null}
                  </td>
                  {/* Propietario */}
                  <Cell value={o.nombre} onChange={setO("nombre")} placeholder="Nombre" />
                  <Cell value={o.apellido} onChange={setO("apellido")} placeholder="Apellido" />
                  <Cell value={o.email} onChange={setO("email")} placeholder="Email" type="email" warn={warnOwner} />
                  <Cell value={o.ci} onChange={setO("ci")} placeholder="C.I." />
                  <Cell value={o.cel} onChange={setO("cel")} placeholder="Celular" />
                  {/* Inquilino */}
                  <Cell value={t.nombre} onChange={setT("nombre")} placeholder="Nombre" />
                  <Cell value={t.apellido} onChange={setT("apellido")} placeholder="Apellido" />
                  <Cell value={t.email} onChange={setT("email")} placeholder="Email" type="email" warn={warnTenant} />
                  <Cell value={t.ci} onChange={setT("ci")} placeholder="C.I." />
                  <Cell value={t.cel} onChange={setT("cel")} placeholder="Celular" />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AdminModal>
  );
};

const thBase = {
  padding: "8px 10px",
  textAlign: "left",
  fontWeight: 600,
  color: colors.white,
  background: "#3b5fc0",
  borderBottom: `2px solid ${colors.white}`,
  whiteSpace: "nowrap",
  fontSize: 11,
  letterSpacing: "0.04em",
};

const thGroup = {
  textAlign: "center",
  fontSize: 11,
  letterSpacing: "0.06em",
};

const tdFixed = {
  padding: "3px 8px",
  borderRight: `1px solid ${colors.gray200}`,
  fontWeight: 500,
  fontSize: 13,
  whiteSpace: "nowrap",
  color: colors.gray700,
  verticalAlign: "middle",
};

export default EditarPropietariosModal;
